import { Dispatch, FC, SetStateAction, useEffect } from 'react';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';
import { getStudentReportStatsAndSkillsWeekly } from '../../graphQLOperations/IndividualStudentReport';
import { IndividualStudentReportStatsQuery } from '../../graphQLOperations/__generated__/IndividualStudentReportStatsQuery.graphql';
import { PartyPopper } from 'lucide-react';
import { useAppSelector } from '../../redux/hooks';
import { selectedIndividualStudentReportState } from '../../redux/reducers/individualStudentReportSlice';
import { isBefore } from 'date-fns';
import { Tooltip } from 'primereact/tooltip';
import { CustomBranding, mapCustomBranding } from '../../models/User';
import { selectUser } from '../../redux/reducers/userSlice';
import { exitSlipCompletedCount } from '../../businessLogicService/exitSlipCompletedCount';
import { exitSlipAverageScore } from '../../businessLogicService/exitSlipAverageScore';

interface Props {
  weeklyStatsRef: PreloadedQuery<IndividualStudentReportStatsQuery, Record<string, unknown>>;
  setInSchoolYear: Dispatch<SetStateAction<boolean>>;
}

export const IndividualReportWeeklyStats: FC<Props> = ({ weeklyStatsRef, setInSchoolYear }) => {
  const { individualReportPerformanceStats, siteSchoolYears, classExitSlips } = usePreloadedQuery(
    getStudentReportStatsAndSkillsWeekly,
    weeklyStatsRef,
  );

  const isrRedux = useAppSelector(selectedIndividualStudentReportState);
  const userRedux = useAppSelector(selectUser);

  const miamiBrand =
    userRedux?.network?.customBranding &&
    mapCustomBranding[userRedux?.network?.customBranding] === CustomBranding.MIAMI;

  useEffect(() => {
    const activeSchoolYear = siteSchoolYears.reduce((max, current) => {
      return current.startOn > max.startOn ? current : max;
    });

    if (isrRedux?.dates?.length && activeSchoolYear) {
      if (isBefore(activeSchoolYear.startOn, isrRedux.dates[0])) {
        return setInSchoolYear(false);
      } else {
        return setInSchoolYear(true);
      }
    }
    return setInSchoolYear(true);
  }, [siteSchoolYears, isrRedux?.dates]);

  return (
    <div className="tw-border-1 tw-mt-6">
      <div className="tw-w-full">
        <h4>Weekly performance stats:</h4>
        <div
          className={`tw-flex tw-mt-4 tw-justify-center tw-items-center tw-gap-x-4 ${
            miamiBrand ? 'tw-flex-col tw-gap-y-4' : ' tw-flex-col lg:tw-flex-row lg:justify-between'
          }`}
        >
          <div className={`tw-flex tw-flex-row ${miamiBrand ? 'tw-w-full' : 'tw-w-[60%]'}  tw-gap-x-2`}>
            <div
              className={`tw-border-[3px] ${
                miamiBrand ? 'tw-w-[30%] lg:tw-w-[40%]' : 'tw-w-[25%]'
              } tw-border-neutral-100 tw-border-solid tw-rounded-[16px] tw-py-2 tw-px-8 tw-h-[10rem] `}
            >
              <p className="tw-text-lg tw-text-center tw-text-sm tw-mt-8 tw-font-atkinson">Skills worked on</p>
              <p className="tw-text-xl tw-text-center tw-font-bold">
                {individualReportPerformanceStats.skillsWorkedOn}
              </p>
            </div>

            <div
              className={`tw-flex ${
                miamiBrand ? 'tw-w-[70%] lg:tw-w-[60%]' : 'tw-w-[75%]'
              } tw-justify-between tw-border-[3px] tw-border-neutral-100 tw-rounded-[16px] tw-border-solid tw-h-[10rem]`}
            >
              <div className="tw-w-[100%]">
                <p className="tw-text-center tw-font-atkinson" id="isrDemonstratedSkillsText">
                  Newly Demonstrated Skills
                </p>
                <Tooltip position="top" target="#isrDemonstratedSkillsText">
                  <div>
                    <p className="tw-text-center tw-text-neutral-300">
                      <b className="tw-font-atkinson">
                        {`Counts skills demonstrated for the first time (first pass or inference). Skills already
                      demonstrated in previous weeks are not counted again.`}
                      </b>
                    </p>
                  </div>
                </Tooltip>
                <div className="tw-flex tw-h-[7rem]">
                  <div className="tw-border-2 tw-mb-3 tw-mx-2 tw-border-neutral-100 tw-border-solid tw-rounded-lg tw-w-[50%]">
                    <p className="tw-text-center tw-text-sm tw-font-atkinson">Directly passed</p>
                    <p className="tw-text-xl tw-text-center tw-font-bold">
                      {individualReportPerformanceStats.demonstratedSkillsPassed}
                    </p>
                  </div>
                  <div className="tw-flex tw-mt-4 tw-text-neutral-400">+</div>
                  <div className="tw-border-2 tw-mb-3 tw-mx-2 tw-border-neutral-100 tw-border-solid tw-rounded-lg tw-w-[50%] ">
                    <p className="tw-text-center tw-text-sm">Passed via inference</p>
                    <p className="tw-text-xl tw-text-center tw-font-bold">
                      {individualReportPerformanceStats.demonstratedSkillsInferred}
                    </p>
                  </div>
                </div>
              </div>
              <div className="tw-flex tw-items-center tw-mb-3 tw-mt-3">
                <div className="tw-mr-4 tw-text-lg tw-text-neutral-400">=</div>
                <div className="tw-border-2 tw-mr-2 tw-flex tw-justify-center tw-mt-2 tw-h-full tw-border-neutral-100 tw-border-solid tw-rounded-lg tw-w-[10rem]">
                  <div className="tw-mt-2">
                    <p className="tw-text-lg tw-text-center tw-text-sm tw-w-full">Weekly pace</p>
                    <p
                      className={`tw-text-xl tw-text-center tw-font-bold tw-w-full ${
                        individualReportPerformanceStats.demonstratedSkillsInferred +
                          individualReportPerformanceStats.demonstratedSkillsPassed >
                        2
                          ? 'tw-text-emerald-500'
                          : ''
                      }`}
                    >
                      {individualReportPerformanceStats.demonstratedSkillsInferred +
                        individualReportPerformanceStats.demonstratedSkillsPassed}
                    </p>
                    {individualReportPerformanceStats.demonstratedSkillsInferred +
                      individualReportPerformanceStats.demonstratedSkillsPassed >=
                      2 && (
                      <div className="tw-flex tw-items-center" style={{ position: 'relative', top: -10 }}>
                        <PartyPopper size={20} className="tw-text-emerald-500 tw-mt-0 tw-pt-0" />
                        <span className="tw-text-emerald-500 tw-font-semibold tw-ml-2">
                          {individualReportPerformanceStats.demonstratedSkillsInferred +
                            individualReportPerformanceStats.demonstratedSkillsPassed >
                          2
                            ? 'Exceeded'
                            : individualReportPerformanceStats.demonstratedSkillsInferred +
                                individualReportPerformanceStats.demonstratedSkillsPassed ===
                              2
                            ? 'Weekly pace met'
                            : ''}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={`tw-flex tw-flex-row ${miamiBrand ? 'tw-w-full ' : 'tw-w-[40%]'}  tw-gap-x-2`}>
            {miamiBrand && (
              <div
                className={`tw-flex tw-justify-between tw-border-[3px] tw-border-blue-50 tw-rounded-[16px] tw-border-solid tw-h-[10rem] tw-w-[33%]`}
              >
                <div className="tw-w-full">
                  <p className="tw-text-center">Exit Ticket</p>
                  <div className="tw-flex tw-h-[7rem]">
                    <div className="tw-border-2 tw-mb-3 tw-mx-2 tw-border-neutral-100 tw-border-solid tw-rounded-lg tw-w-[50%]">
                      <p className="tw-text-center tw-text-sm">Completed</p>
                      <p className="tw-text-xl tw-text-center tw-font-bold">{exitSlipCompletedCount(classExitSlips)}</p>
                    </div>
                    <div className="tw-border-2 tw-mb-3 tw-mx-2 tw-border-neutral-100 tw-border-solid tw-rounded-lg tw-w-[50%] ">
                      <p className="tw-text-center tw-text-sm">Avg. score</p>
                      <p className="tw-text-xl tw-text-center tw-font-bold">{exitSlipAverageScore(classExitSlips)}%</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div
              className={`tw-border-[3px] tw-border-neutral-100 tw-border-solid tw-rounded-[16px] tw-py-2 tw-px-8 tw-h-[10rem]  ${
                miamiBrand ? 'tw-w-[33%]' : 'tw-w-[50%]'
              }`}
            >
              <p className="tw-text-lg tw-text-center tw-text-sm tw-mt-8">Login</p>
              <p className="tw-text-xl tw-text-center tw-font-bold">
                {individualReportPerformanceStats.logins}{' '}
                {individualReportPerformanceStats.logins > 1 || individualReportPerformanceStats.logins === 0
                  ? 'times'
                  : 'time'}
              </p>
            </div>

            <div
              className={`tw-border-[3px] tw-border-neutral-100 tw-border-solid tw-rounded-[16px] tw-py-2 tw-px-8 tw-h-[10rem] ${
                miamiBrand ? 'tw-w-[33%]' : 'tw-w-[50%]'
              }`}
            >
              <p className="tw-text-lg tw-text-center tw-text-sm tw-mt-8">Platform time</p>
              <p className="tw-text-xl tw-text-center tw-font-bold">
                {individualReportPerformanceStats.platformTime} min
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
